/**
 * Anemoia waitlist (custom).
 *
 * Drives <anemoia-waitlist>: reads the ?product= param (the locked exhibit that
 * was clicked in the golden-frame hero) into the heading + a hidden field, picks
 * the right A/B/C variation carousel out of the ones the section rendered, wires
 * the price range slider (live value + filled track), and submits the form as
 * JSON to a Cloudflare Worker (data-worker-url) which creates the Omnisend
 * contact server-side. The Omnisend private API key never touches the browser.
 *
 * Two image paths live here, and only one runs per page load:
 *   - variations: server-rendered slides, one set per product, chosen by ?handle=.
 *   - classic:    the old single figure, whose layers are fetched and built from
 *                 /products/<handle>.js. Still the path for any product with no
 *                 variations configured.
 */

const SWIPE_THRESHOLD = 40;

class AnemoiaWaitlist extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-awl-form]');
    this.workerUrl = this.dataset.workerUrl || '';
    this.currency = this.dataset.currency || '$';

    this.params = this.readParams();
    this.usingVariations = this.setupVariations();
    this.setupProduct();
    this.setupRange();
    this.setupSubmit();
  }

  readParams() {
    try {
      const params = new URLSearchParams(window.location.search);
      return {
        product: (params.get('product') || '').trim(),
        handle: (params.get('handle') || '').trim(),
      };
    } catch (error) {
      return { product: '', handle: '' };
    }
  }

  /** Pull ?product= / ?handle= into the heading, hidden field, and image gallery. */
  setupProduct() {
    const { product, handle } = this.params;

    const hidden = this.querySelector('[data-awl-product]');
    if (hidden) hidden.value = product;

    if (product) this.setHeading(product);
    if (handle && !this.usingVariations) this.setupGallery(handle, product);
  }

  setHeading(name) {
    const heading = this.querySelector('[data-awl-title]');
    const template = this.dataset.headingProduct || 'Join the waitlist for {product}';
    if (heading) heading.textContent = template.replace('{product}', name);
  }

  /**
   * Pick the variation set for the product this visit is about.
   *
   * The section renders a hidden carousel + chip fieldset for EVERY product it was
   * given, because metafields are server-side only — the page cannot go and fetch
   * variations for whatever ?handle= turns up. So the choice is made here, in the
   * DOM. Returns whether the carousel took over from the classic gallery.
   */
  setupVariations() {
    const sets = Array.from(this.querySelectorAll('[data-awl-carousel]'));
    const fields = Array.from(this.querySelectorAll('[data-awl-varfield]'));
    if (!sets.length) return false;

    const { product, handle } = this.params;
    let active = null;
    if (handle) {
      active = sets.find((el) => (el.dataset.awlHandle || '').toLowerCase() === handle.toLowerCase()) || null;
    } else if (product) {
      active = sets.find((el) => (el.dataset.awlTitle || '').toLowerCase() === product.toLowerCase()) || null;
    } else {
      // A direct visit naming no product: the first listed set is the page's subject.
      active = sets[0];
    }

    // Anything not on screen is REMOVED, not hidden. A leftover chip fieldset would
    // let a radio for the wrong product ride along in the submission.
    const keep = active ? active.dataset.awlHandle : null;
    sets.forEach((el) => {
      if (el !== active) el.remove();
    });
    fields.forEach((el) => {
      if (el.dataset.awlHandle !== keep) el.remove();
    });

    // A ?handle= naming some other locked product falls back to its own gallery.
    if (!active) {
      this.querySelector('[data-awl-varname]')?.remove();
      this.querySelector('[data-awl-varstatus]')?.remove();
      this.querySelector('[data-awl-classic]')?.removeAttribute('hidden');
      return false;
    }

    active.removeAttribute('hidden');
    this.querySelector('[data-awl-varfield]')?.removeAttribute('hidden');
    this.wireCarousel(active);

    // setupGallery() used to supply the heading from the fetched title when ?product=
    // was missing. The carousel path never fetches, so read it out of the markup.
    if (!product && active.dataset.awlTitle) this.setHeading(active.dataset.awlTitle);
    return true;
  }

  wireCarousel(root) {
    this.carousel = root;
    this.slides = Array.from(root.querySelectorAll('[data-awl-slide]'));
    this.chips = Array.from(this.querySelectorAll('[data-awl-chip]'));
    this.varNameEl = this.querySelector('[data-awl-varname]');
    this.varStatusEl = this.querySelector('[data-awl-varstatus]');
    this.vIndex = 0;
    if (!this.slides.length) return;

    // One crossfade controller per slide, so only the visible one steps on a click.
    this.galleries = this.slides.map((figure) =>
      this.wireGallery(figure, figure.querySelectorAll('.awl__gimg').length)
    );

    root.querySelectorAll('[data-awl-step]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.goToVariation(this.vIndex + Number(btn.dataset.awlStep));
      });
    });

    // Bound on the carousel, NOT on the host element: the "how much would you pay"
    // range input is driven with the arrow keys, and a host-level handler calling
    // preventDefault() would eat them.
    root.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.goToVariation(this.vIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });

    this.wireSwipe(root);

    // Clicking a chip is the ONLY thing that records an answer. The arrows move the
    // carousel and mark what's on screen; they never check a radio.
    this.chips.forEach((chip) => {
      const input = chip.querySelector('[data-awl-varinput]');
      if (!input) return;
      input.addEventListener('change', () => this.goToVariation(Number(input.dataset.awlIndex)));
    });

    root.classList.add('is-ready');
    this.renderVariationState();
  }

  /** Axis-locked horizontal swipe between variations, same threshold as the hero. */
  wireSwipe(root) {
    let startX = 0;
    let startY = 0;
    let axis = null;

    root.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.changedTouches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        axis = null;
        this.suppressTap = false;
      },
      { passive: true }
    );

    root.addEventListener(
      'touchmove',
      (event) => {
        if (axis) return;
        const touch = event.changedTouches[0];
        const dx = Math.abs(touch.clientX - startX);
        const dy = Math.abs(touch.clientY - startY);
        if (dx > 8 || dy > 8) axis = dx > dy ? 'x' : 'y';
      },
      { passive: true }
    );

    root.addEventListener('touchend', (event) => {
      if (axis !== 'x') return;
      const dx = event.changedTouches[0].clientX - startX;
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      // The browser fires a click on the figure at the end of this swipe, and the
      // figure's own tap handler advances its images. Without the flag, one swipe
      // would change slide AND skip the new slide's first image.
      this.suppressTap = true;
      this.goToVariation(this.vIndex + (dx < 0 ? 1 : -1));
    });

    // Capture, so it runs before the figure's own click listener and can stop it.
    root.addEventListener(
      'click',
      (event) => {
        if (!this.suppressTap) return;
        this.suppressTap = false;
        event.stopPropagation();
        event.preventDefault();
      },
      true
    );
  }

  goToVariation(target) {
    if (!this.slides || this.slides.length < 2) return;
    const n = (target + this.slides.length) % this.slides.length;
    if (n === this.vIndex) return;

    // Rewind the outgoing slide: a hidden slide must not come back part-way
    // through its sequence.
    this.galleries[this.vIndex]?.reset();
    this.slides[this.vIndex].classList.remove('is-active');
    this.slides[this.vIndex].setAttribute('aria-hidden', 'true');
    this.slides[n].classList.add('is-active');
    this.slides[n].removeAttribute('aria-hidden');
    this.vIndex = n;
    this.renderVariationState();
  }

  renderVariationState() {
    const slide = this.slides[this.vIndex];
    if (!slide) return;
    const label = slide.dataset.awlLabel || '';
    const name = slide.dataset.awlName || '';

    if (this.varNameEl) {
      this.varNameEl.textContent = name;
      this.varNameEl.hidden = name === '';
    }
    if (this.varStatusEl) {
      this.varStatusEl.textContent = `Variation ${label}${name ? `, ${name}` : ''} — ${
        this.vIndex + 1
      } of ${this.slides.length}`;
    }
  }

  /** Fetch the clicked product's own images and crossfade them on click. */
  async setupGallery(handle, product) {
    const figure = this.querySelector('[data-awl-figure]');
    if (!figure) return;

    let data;
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      data = await res.json();
    } catch (error) {
      console.warn('Anemoia waitlist: could not load product images', error);
      return;
    }

    const images = Array.isArray(data.images) ? data.images.slice(0, 6) : [];
    if (!images.length) return;

    // No product name in the URL? Fall back to the fetched title.
    if (!product && data.title) this.setHeading(data.title);

    const frag = document.createDocumentFragment();
    images.forEach((src, i) => {
      const img = document.createElement('img');
      img.className = `awl__gimg${i === 0 ? ' is-shown' : ''}`;
      img.src = this.sizedImage(src);
      img.alt = data.title || '';
      img.loading = 'lazy';
      frag.appendChild(img);
    });

    // Hide the fallback (img or placeholder svg) and drop in the gallery.
    figure.querySelectorAll(':scope > *').forEach((el) => {
      el.style.display = 'none';
    });
    figure.appendChild(frag);
    figure.classList.add('is-dynamic');

    this.wireGallery(figure, images.length);
  }

  /** Shopify CDN URL → https + a sensible width for the card. */
  sizedImage(src) {
    let url = src.startsWith('//') ? `https:${src}` : src;
    url += `${url.includes('?') ? '&' : '?'}width=700`;
    return url;
  }

  /**
   * Cycle through one figure's images. Every pointer works the same way now: a
   * click/tap advances one image, and nothing moves on its own. Hover is left to
   * do one thing only — CSS lifts the grey off the picture while the cursor is on
   * it. Touch has no hover, so the first tap latches that colour on instead.
   *
   * Returns a { stop, reset } controller so the carousel can park a slide it is
   * moving away from. Used by both image paths — the classic one builds its layers
   * first, the carousel gets them server-rendered.
   */
  wireGallery(figure, count) {
    const noop = { stop() {}, reset() {} };
    if (count < 2) return noop;
    const layers = Array.from(figure.querySelectorAll('.awl__gimg'));
    const latchColour = !window.matchMedia('(pointer: fine)').matches;
    let idx = 0;
    const show = (n) => {
      layers[idx].classList.remove('is-shown');
      idx = (n + count) % count;
      layers[idx].classList.add('is-shown');
    };
    const rewind = () => show(0);

    figure.addEventListener('click', () => {
      if (latchColour) figure.classList.add('is-touched');
      show(idx + 1);
    });

    return {
      stop: rewind,
      reset() {
        rewind();
        figure.classList.remove('is-touched');
      },
    };
  }

  /** Range slider: fill the track up to the thumb + mirror the value in the output. */
  setupRange() {
    this.range = this.querySelector('[data-awl-range]');
    this.priceOut = this.querySelector('[data-awl-price-out]');
    if (!this.range) return;

    const render = () => {
      const min = Number(this.range.min) || 0;
      const max = Number(this.range.max) || 100;
      const val = Number(this.range.value) || 0;
      const pct = max > min ? ((val - min) / (max - min)) * 100 : 0;
      this.range.style.setProperty('--awl-fill', `${pct}%`);
      if (this.priceOut) this.priceOut.textContent = `${this.currency}${val}`;
    };
    this.range.addEventListener('input', render);
    render();
  }

  setupSubmit() {
    if (!this.form) return;
    this.submitBtn = this.querySelector('[data-awl-submit]');
    this.successEl = this.querySelector('[data-awl-success]');
    this.errorEl = this.querySelector('[data-awl-error]');

    this.form.addEventListener('submit', async (event) => {
      event.preventDefault();
      this.hideStatus();

      // Honeypot: a filled "company" field means a bot — pretend success, drop it.
      if (this.form.company && this.form.company.value.trim() !== '') {
        this.showSuccess();
        return;
      }

      if (!this.form.reportValidity()) return;

      if (!this.workerUrl) {
        this.showError('Waitlist isn’t connected yet. Please try again later.');
        console.warn('Anemoia waitlist: no data-worker-url set on the section.');
        return;
      }

      const payload = {
        product: this.form.product?.value?.trim() || '',
        name: this.form.name?.value?.trim() || '',
        email: this.form.email?.value?.trim() || '',
        gotSwag: this.form.gotSwag?.value === 'yes',
        wouldPay: Number(this.range?.value) || null,
        source: 'anemoia-waitlist',
        pageUrl: window.location.href,
      };

      // Only sent when a chip was actually clicked; the key is left off entirely
      // otherwise, so the Worker writes no property — "no opinion" has to stay
      // distinguishable from "chose A".
      //
      // Read the :checked element rather than form.variationPref.value: a group of
      // ONE radio resolves to the input itself, not a RadioNodeList, and reports its
      // value whether or not it is checked. That would invent an answer.
      const picked = this.form.querySelector('[data-awl-varinput]:checked');
      const variationPref = (picked?.value || '').trim();
      if (variationPref) payload.variationPref = variationPref;

      this.setBusy(true);
      try {
        const response = await fetch(this.workerUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          throw new Error(`Waitlist request failed (${response.status})`);
        }
        this.showSuccess();
        this.form.reset();
        this.setupRange();
      } catch (error) {
        console.error('Anemoia waitlist submit failed', error);
        this.showError('Something went wrong. Please try again in a moment.');
      } finally {
        this.setBusy(false);
      }
    });
  }

  setBusy(busy) {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = busy;
    this.submitBtn.classList.toggle('is-busy', busy);
  }

  hideStatus() {
    if (this.successEl) this.successEl.hidden = true;
    if (this.errorEl) this.errorEl.hidden = true;
  }

  showSuccess() {
    if (this.successEl) this.successEl.hidden = false;
    // Collapse the form to just the success note (see .awl__form.is-done in CSS).
    if (this.form) this.form.classList.add('is-done');
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }
}

if (!customElements.get('anemoia-waitlist')) {
  customElements.define('anemoia-waitlist', AnemoiaWaitlist);
}
